import { useTraduccion } from '@tallerya/i18n';
import {
  AvisoError,
  Boton,
  CampoNumero,
  CampoSegmentado,
  CampoSeleccion,
  CampoTexto,
  Chip,
  useAvisos,
  useBusquedaDiferida,
  useFormulario,
  Ventana,
} from '@tallerya/ui';
import { useState } from 'react';
import { numeroDe } from '../productos-servicios/datos';
import {
  useAsignables,
  useBuscarEnCatalogo,
  useCambiosLinea,
  useClaveDeReintento,
  type CabeceraOrden,
  type ItemDeCatalogo,
  type LineaOrden,
} from './datos';

// Las ventanas de la sección «Trabajo» (#133): agregar una línea, cambiarle la
// cantidad, asignarla, ligar una anotación al catálogo y quitarla.
//
// Ninguna tiene frame en el Figma: siguen a las de la cabecera (`EditarCabecera`,
// `AccionesDeEstado`). Cada una arranca de cero al abrirse —se monta con la
// línea como `key`— y lo que la API rechace se dice adentro, con lo escrito
// todavía en pantalla.

/** El error de una cantidad escrita, o nada si sirve: mayor que cero y con dos decimales como mucho. */
function errorDeCantidad(texto: string): { quantity?: string } {
  const n = numeroDe(texto);
  if (n === null) return {}; // vacía: lo dice `obligatorio`
  if (Number.isNaN(n)) return { quantity: 'validation.format' };
  if (n <= 0) return { quantity: 'validation.too_small' };
  // Sobre el texto de dos decimales y no multiplicando: `1.1 * 100` no da 110.
  return Number(n.toFixed(2)) === n ? {} : { quantity: 'validation.format' };
}

/** Cómo se escribe la cantidad que ya tiene la línea: `2`, `1.5`. */
const cantidadDe = (linea: LineaOrden) => (linea.quantity === null ? '1' : String(Number(linea.quantity)));

// ------------------------------------------------------------ El buscador del catálogo

/**
 * Un campo que busca en el catálogo mientras se escribe, con la forma de
 * `SelectorDeCliente`: la lista de coincidencias debajo, y el elegido en su
 * lugar con «Cambiar». El texto es de quien lo usa: en «Agregar línea», lo
 * escrito sin elegir nada es la anotación.
 */
function BuscadorDeCatalogo({
  id,
  etiqueta,
  placeholder,
  texto,
  onTexto,
  elegido,
  onElegir,
  onQuitar,
  error,
}: {
  id: string;
  etiqueta: string;
  placeholder: string;
  texto: string;
  onTexto: (texto: string) => void;
  elegido: ItemDeCatalogo | null;
  onElegir: (item: ItemDeCatalogo) => void;
  onQuitar: () => void;
  error?: string | undefined;
}) {
  const { t } = useTraduccion();
  const [buscado, setBuscado] = useState('');
  const resultados = useBuscarEnCatalogo(buscado);

  useBusquedaDiferida(texto, setBuscado, { pendiente: texto !== buscado });

  if (elegido) {
    return (
      <div className={`ui-campo ui-campo--completo${error ? ' ui-campo--error' : ''}`}>
        <span className="ui-campo__etiqueta">{etiqueta}</span>
        <div className="activos__cliente">
          <Chip tono={elegido.kind === 'service' ? 'marca' : 'neutro'}>
            {t(`admin.ordenes.lineas.clase.${elegido.kind}`)}
          </Chip>
          <strong>{elegido.name}</strong>
          <Boton id={id} variante="texto" onClick={onQuitar}>
            {t('admin.ordenes.lineas.cambiarItem')}
          </Boton>
        </div>
        {/* El ítem que se apagó entre buscarlo y guardar: el error es de lo elegido. */}
        {error && <p className="ui-campo__ayuda">{error}</p>}
      </div>
    );
  }

  const lista = buscado.trim().length >= 2 ? (resultados.data ?? []) : [];

  return (
    <div className={`ui-campo ui-campo--completo${error ? ' ui-campo--error' : ''}`}>
      <label htmlFor={id} className="ui-campo__etiqueta">
        {etiqueta}
      </label>
      <div className="ui-campo__caja">
        <input
          id={id}
          value={texto}
          autoComplete="off"
          placeholder={placeholder}
          aria-describedby={`${id}-ayuda`}
          onChange={(e) => onTexto(e.target.value)}
        />
      </div>
      {lista.length > 0 && (
        <ul className="activos__resultados" aria-label={t('admin.ordenes.lineas.resultados')}>
          {lista.map((item) => (
            <li key={String(item.id)}>
              <button type="button" className="activos__resultado" onClick={() => onElegir(item)}>
                <span>{item.name}</span>
                <small>{[t(`admin.ordenes.lineas.clase.${item.kind}`), item.code].join(' · ')}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* Una búsqueda que falla no es «no hay coincidencias»: se dice. */}
      {(error ?? resultados.isError) && (
        <p id={`${id}-ayuda`} className="ui-campo__ayuda">
          {error ?? t('admin.ordenes.lineas.errorBusqueda')}
        </p>
      )}
    </div>
  );
}

// ------------------------------------------------------------ Agregar

interface PropsAgregar {
  orden: string;
  /** `trabajo`: un servicio o un producto, del catálogo o libre. `anotacion`: solo texto. */
  modo: 'trabajo' | 'anotacion';
  onCerrar: () => void;
  /** Tras agregar bien: la sección quita el aviso de lo que se había rechazado. */
  onAgregada: () => void;
}

export function AgregarLinea({ abierta, ...props }: PropsAgregar & { abierta: boolean }) {
  if (!abierta) return null;
  return <ContenidoAgregar key={props.modo} {...props} />;
}

interface ValoresAgregar {
  texto: string;
  quantity: string;
  /** La clase de una línea libre: vacía mientras no se elija. */
  kind: '' | 'service' | 'product';
}

/**
 * **Trabajo** (#162): se escribe, y si lo escrito está en el catálogo se elige de
 * la lista —con su clase y su precio—. Si no está, se dice qué es —servicio o
 * producto— y entra como línea libre, sin precio: el total queda incompleto
 * hasta que alguien se lo ponga, y se liga al catálogo si se da de alta después.
 *
 * **Anotación**: solo el texto. Antes era «lo escrito sin elegir nada», una regla
 * implícita; ahora tiene su propio botón.
 *
 * **No se cierra al agregar**: una orden lleva varias líneas, y abrir la ventana
 * por cada una es un viaje de más. Queda lista para la siguiente.
 */
function ContenidoAgregar({ orden, modo, onCerrar, onAgregada }: PropsAgregar) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosLinea(orden);
  const claveDe = useClaveDeReintento();
  const [elegido, setElegido] = useState<ItemDeCatalogo | null>(null);
  // El id de la línea lo pone quien la crea (#132): el mismo mientras se
  // reintenta, otro para la siguiente.
  const [idLinea, setIdLinea] = useState(() => crypto.randomUUID());
  const esTrabajo = modo === 'trabajo';

  const form = useFormulario<ValoresAgregar>({
    inicial: { texto: '', quantity: '1', kind: '' },
    validar: (v) => ({
      ...(!elegido && !v.texto.trim() && { texto: 'validation.required' }),
      ...(esTrabajo && !elegido && !v.kind && { kind: 'validation.required' }),
      ...(esTrabajo && errorDeCantidad(v.quantity)),
    }),
    guardar: (v) => {
      const envio = {
        id: idLinea,
        catalogItemId: elegido ? String(elegido.id) : '',
        // Con ítem, lo escrito para encontrarlo no es un dictado: no se guarda.
        rawText: elegido ? '' : v.texto,
        kind: esTrabajo && !elegido && v.kind ? v.kind : null,
        quantity: esTrabajo ? numeroDe(v.quantity) : null,
      };
      return cambios.agregar(claveDe(envio), envio);
    },
    camposPorCodigo: {
      'service_order.item_no_encontrado': 'texto',
      'service_order.item_apagado': 'texto',
      'service_order.monto_fuera_de_rango': 'quantity',
    },
  });

  async function agregar() {
    if (!(await form.enviar())) return;
    avisar({ titulo: t('admin.ordenes.lineas.aviso.agregada') });
    onAgregada();
    setElegido(null);
    setIdLinea(crypto.randomUUID());
    form.reiniciar();
    requestAnimationFrame(() => document.getElementById(form.idDe('texto'))?.focus());
  }

  const libre = esTrabajo && !elegido && form.valores.texto.trim() !== '';

  return (
    <Ventana
      abierta
      titulo={t(esTrabajo ? 'admin.ordenes.lineas.agregar.titulo' : 'admin.ordenes.lineas.anotar.titulo')}
      info={t(esTrabajo ? 'admin.ordenes.lineas.agregar.info' : 'admin.ordenes.lineas.anotar.info')}
      ancho={560}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={onCerrar}>
            {t('admin.ordenes.lineas.cerrar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void agregar()}>
            {t('admin.ordenes.lineas.agregar.boton')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      {esTrabajo ? (
        <BuscadorDeCatalogo
          id={form.idDe('texto')}
          etiqueta={t('admin.ordenes.lineas.agregar.campo')}
          placeholder={t('admin.ordenes.lineas.agregar.ejemplo')}
          texto={form.valores.texto}
          onTexto={(texto) => form.cambiar('texto', texto)}
          elegido={elegido}
          onElegir={setElegido}
          onQuitar={() => {
            setElegido(null);
            form.cambiar('texto', '');
          }}
          error={form.errores.texto}
        />
      ) : (
        <CampoTexto
          form={form}
          nombre="texto"
          etiqueta={t('admin.ordenes.lineas.anotar.campo')}
          placeholder={t('admin.ordenes.lineas.anotar.ejemplo')}
          autoComplete="off"
          obligatorio
          ancho="completo"
        />
      )}
      {libre && (
        <CampoSegmentado
          form={form}
          nombre="kind"
          etiqueta={t('admin.ordenes.lineas.libre.clase')}
          info={t('admin.ordenes.lineas.libre.info')}
          obligatorio
          opciones={[
            { valor: 'service', etiqueta: t('admin.ordenes.lineas.clase.service') },
            { valor: 'product', etiqueta: t('admin.ordenes.lineas.clase.product') },
          ]}
        />
      )}
      {(elegido || libre) && (
        <CampoNumero
          form={form}
          nombre="quantity"
          etiqueta={t('admin.ordenes.lineas.cantidad')}
          decimales={2}
          obligatorio
          ancho="corto"
        />
      )}
    </Ventana>
  );
}

// ------------------------------------------------------------ Cambiar la cantidad

interface PropsDeLinea<O> {
  orden: O;
  onCerrar: () => void;
  onGuardada: () => void;
}

export function CantidadLinea({ linea, ...props }: PropsDeLinea<string> & { linea: LineaOrden | null }) {
  if (!linea) return null;
  return <ContenidoCantidad key={String(linea.id)} linea={linea} {...props} />;
}

function ContenidoCantidad({ orden, linea, onCerrar, onGuardada }: PropsDeLinea<string> & { linea: LineaOrden }) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosLinea(orden);
  const claveDe = useClaveDeReintento();

  const form = useFormulario<{ quantity: string }>({
    inicial: { quantity: cantidadDe(linea) },
    validar: (v) => errorDeCantidad(v.quantity),
    guardar: (v) => {
      const cantidad = numeroDe(v.quantity) ?? 1;
      return cambios.cambiarCantidad(claveDe({ linea: linea.id, cantidad }), String(linea.id), cantidad);
    },
    camposPorCodigo: { 'service_order.monto_fuera_de_rango': 'quantity' },
  });

  async function guardar() {
    if (!(await form.enviar())) return;
    avisar({ titulo: t('admin.ordenes.aviso.guardada') });
    onGuardada();
    onCerrar();
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.ordenes.lineas.cambiarCantidad')}
      descripcion={linea.catalogItemName ?? linea.rawText}
      acciones={<Pie guardando={form.guardando} onCerrar={onCerrar} onGuardar={() => void guardar()} />}
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoNumero
        form={form}
        nombre="quantity"
        etiqueta={t('admin.ordenes.lineas.cantidad')}
        decimales={2}
        obligatorio
        ancho="corto"
      />
    </Ventana>
  );
}

// ------------------------------------------------------------ Asignar

export function AsignarLinea({ linea, ...props }: PropsDeLinea<CabeceraOrden> & { linea: LineaOrden | null }) {
  if (!linea) return null;
  return <ContenidoAsignar key={String(linea.id)} linea={linea} {...props} />;
}

function ContenidoAsignar({
  orden,
  linea,
  onCerrar,
  onGuardada,
}: PropsDeLinea<CabeceraOrden> & { linea: LineaOrden }) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosLinea(String(orden.id));
  const claveDe = useClaveDeReintento();
  const asignables = useAsignables(String(orden.locationId));

  const form = useFormulario<{ assignedTo: string }>({
    inicial: { assignedTo: linea.assignedTo ? String(linea.assignedTo) : '' },
    guardar: (v) =>
      cambios.asignar(claveDe({ linea: linea.id, persona: v.assignedTo }), String(linea.id), v.assignedTo),
    camposPorCodigo: {
      'service_order.personal_no_encontrado': 'assignedTo',
      'service_order.personal_apagado': 'assignedTo',
    },
  });

  // Quien ya la tenía se ofrece aunque ya no cubra la sucursal, como en la cabecera.
  const opciones = [
    ...(asignables.data ?? []).map((p) => ({ valor: String(p.id), etiqueta: p.name })),
    ...(linea.assignedTo && !(asignables.data ?? []).some((p) => p.id === linea.assignedTo)
      ? [{ valor: String(linea.assignedTo), etiqueta: linea.assignedToName ?? '' }]
      : []),
  ];

  async function guardar() {
    if (!(await form.enviar())) return;
    avisar({ titulo: t('admin.ordenes.aviso.guardada') });
    onGuardada();
    onCerrar();
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.ordenes.lineas.asignar.titulo')}
      info={t('admin.ordenes.lineas.asignar.info')}
      descripcion={linea.catalogItemName ?? linea.rawText}
      acciones={<Pie guardando={form.guardando} onCerrar={onCerrar} onGuardar={() => void guardar()} />}
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoSeleccion
        form={form}
        nombre="assignedTo"
        etiqueta={t('admin.ordenes.asignarA')}
        placeholder={t('admin.ordenes.lineas.asignar.nadie')}
        opciones={opciones}
        ancho="completo"
      />
    </Ventana>
  );
}

// ------------------------------------------------------------ Ligar al catálogo

export function LigarLinea({ linea, ...props }: PropsDeLinea<string> & { linea: LineaOrden | null }) {
  if (!linea) return null;
  return <ContenidoLigar key={String(linea.id)} linea={linea} {...props} />;
}

/** «Esto que se escribió era el cambio de aceite»: la anotación toma la clase y el precio del ítem, y conserva su texto. */
function ContenidoLigar({ orden, linea, onCerrar, onGuardada }: PropsDeLinea<string> & { linea: LineaOrden }) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosLinea(orden);
  const claveDe = useClaveDeReintento();
  const [elegido, setElegido] = useState<ItemDeCatalogo | null>(null);

  const form = useFormulario<{ texto: string }>({
    inicial: { texto: '' },
    validar: () => (elegido ? {} : { texto: 'validation.required' }),
    guardar: () => {
      const item = elegido ? String(elegido.id) : '';
      return cambios.ligar(claveDe({ linea: linea.id, item }), String(linea.id), item);
    },
    camposPorCodigo: {
      'service_order.item_no_encontrado': 'texto',
      'service_order.item_apagado': 'texto',
    },
  });

  async function guardar() {
    if (!(await form.enviar())) return;
    avisar({ titulo: t('admin.ordenes.lineas.aviso.ligada') });
    onGuardada();
    onCerrar();
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.ordenes.lineas.ligar')}
      info={t('admin.ordenes.lineas.ligar.info')}
      descripcion={`«${linea.rawText ?? ''}»`}
      ancho={560}
      acciones={
        <Pie
          guardando={form.guardando}
          etiqueta={t('admin.ordenes.lineas.ligar.boton')}
          onCerrar={onCerrar}
          onGuardar={() => void guardar()}
        />
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <BuscadorDeCatalogo
        id={form.idDe('texto')}
        etiqueta={t('admin.ordenes.lineas.ligar.campo')}
        placeholder={t('admin.ordenes.lineas.ligar.ejemplo')}
        texto={form.valores.texto}
        onTexto={(texto) => form.cambiar('texto', texto)}
        elegido={elegido}
        onElegir={setElegido}
        onQuitar={() => {
          setElegido(null);
          form.cambiar('texto', '');
        }}
        error={form.errores.texto}
      />
    </Ventana>
  );
}

// ------------------------------------------------------------ Quitar

/** Quitar no borra: la línea deja de verse y de contar, y lo que decía queda en la base. */
export function QuitarLinea({
  linea,
  onCerrar,
  onQuitar,
}: {
  linea: LineaOrden | null;
  onCerrar: () => void;
  /** Quita y cierra. Lo que la API rechace lo dice la sección. */
  onQuitar: (linea: LineaOrden) => Promise<void>;
}) {
  const { t } = useTraduccion();
  const [enCurso, setEnCurso] = useState(false);

  if (!linea) return null;

  return (
    <Ventana
      abierta
      titulo={t('admin.ordenes.lineas.quitar.titulo')}
      descripcion={t('admin.ordenes.lineas.quitar.texto', { linea: linea.catalogItemName ?? linea.rawText ?? '' })}
      acciones={
        <>
          <Boton variante="texto" disabled={enCurso} onClick={onCerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton
            variante="peligro"
            disabled={enCurso}
            aria-busy={enCurso}
            onClick={() => {
              setEnCurso(true);
              void onQuitar(linea).finally(() => setEnCurso(false));
            }}
          >
            {t('admin.ordenes.lineas.quitar')}
          </Boton>
        </>
      }
    />
  );
}

/** El pie de las ventanas que guardan: «Cancelar» y la acción. También lo usan las de montos. */
export function Pie({
  guardando,
  etiqueta,
  onCerrar,
  onGuardar,
}: {
  guardando: boolean;
  etiqueta?: string;
  onCerrar: () => void;
  onGuardar: () => void;
}) {
  const { t } = useTraduccion();

  return (
    <>
      <Boton variante="texto" disabled={guardando} onClick={onCerrar}>
        {t('app.cancelar')}
      </Boton>
      <Boton disabled={guardando} aria-busy={guardando} onClick={onGuardar}>
        {guardando ? t('ui.formulario.guardando') : (etiqueta ?? t('app.guardar'))}
      </Boton>
    </>
  );
}
